import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { MonthOption } from "@/lib/month-options";
import { cn } from "@/lib/utils";

/** Radix `Select` cannot hold an empty-string value, so "no month" needs a sentinel. */
const NONE = "__none__";

interface Props {
  /** `YYYY-MM`, or `""` / `null` for no month. */
  value: string | null;
  onValueChange: (month: string) => void;
  options: readonly MonthOption[];
  /**
   * When set, the list opens with this choice and picking it clears the month
   * (`onValueChange("")`). Leave unset for a month that is required.
   */
  noneLabel?: string;
  placeholder?: string;
  id?: string;
  "aria-label"?: string;
  size?: "sm" | "default";
  disabled?: boolean;
  className?: string;
}

/**
 * A month picker that is a plain list — "November 2026", "December 2026" … — in place of
 * the browser's month/year spinner, so the next few months are one click away.
 */
export function MonthSelect({
  value,
  onValueChange,
  options,
  noneLabel,
  placeholder = "Pick a month",
  id,
  "aria-label": ariaLabel,
  size = "default",
  disabled,
  className,
}: Props) {
  const selected = value === null || value === "" ? (noneLabel ? NONE : undefined) : value;
  return (
    <Select
      value={selected}
      onValueChange={(next) => onValueChange(next === NONE ? "" : next)}
      disabled={disabled}
    >
      <SelectTrigger id={id} aria-label={ariaLabel} size={size} className={cn("w-full", className)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {noneLabel !== undefined && <SelectItem value={NONE}>{noneLabel}</SelectItem>}
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
            {o.hint !== undefined && <span className="text-muted-foreground">· {o.hint}</span>}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
